import React, { useState } from 'react';
import {
  Card,
  Button,
  Table,
  Space,
  Typography,
  Modal,
  Form,
  Input,
  InputNumber,
  Popconfirm,
  App,
} from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useNavigate } from 'react-router-dom';
import { spamLinkApi } from '../services/api';
import { SpamLink, CreateSpamLinkDto } from '../types/spam-link';

const { Title } = Typography;

const SpamLinks: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { message: messageApi } = App.useApp();
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [form] = Form.useForm();
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const { data, isLoading } = useQuery(
    ['spam-links', currentPage, pageSize],
    () => spamLinkApi.getAll({ page: currentPage, limit: pageSize }),
    {
      keepPreviousData: true,
    }
  );

  const createMutation = useMutation(
    (data: CreateSpamLinkDto) => spamLinkApi.create(data),
    {
      onSuccess: () => {
        messageApi.success('Spam link created successfully!');
        queryClient.invalidateQueries('spam-links');
        setIsModalVisible(false);
        form.resetFields();
      },
      onError: (error: any) => {
        messageApi.error(error.response?.data?.message || 'Failed to create spam link');
      },
    }
  );

  const deleteMutation = useMutation(
    (id: string) => spamLinkApi.delete(id),
    {
      onSuccess: () => {
        messageApi.success('Spam link deleted successfully!');
        queryClient.invalidateQueries('spam-links');
      },
      onError: (error: any) => {
        messageApi.error(error.response?.data?.message || 'Failed to delete spam link');
      },
    }
  );

  const handleCreate = () => {
    form.resetFields();
    setIsModalVisible(true);
  };

  const handleDelete = (id: string) => {
    deleteMutation.mutate(id);
  };

  const handleSubmit = () => {
    form.validateFields().then((values) => {
      createMutation.mutate(values);
    });
  };

  const handleCancel = () => {
    setIsModalVisible(false);
    form.resetFields();
  };

  const columns = [
    {
      title: 'Domain URL',
      dataIndex: 'domain_url',
      key: 'domain_url',
      render: (text: string) => (
        <a href={text} target="_blank" rel="noopener noreferrer" style={{ color: '#1890ff' }}>
          {text}
        </a>
      ),
    },
    {
      title: 'Target Domains Count',
      dataIndex: 'target_domains_count',
      key: 'target_domains_count',
      align: 'center' as const,
      width: 180,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      align: 'center' as const,
      width: 100,
      render: (status: string) => (
        <span style={{ color: status === 'active' ? '#52c41a' : '#999' }}>
          {status || 'active'}
        </span>
      ),
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      render: (text: string) => text || '-',
    },
    {
      title: 'Created',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 120,
      render: (date: string) => date ? new Date(date).toLocaleDateString() : 'N/A',
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 150,
      align: 'center' as const,
      render: (_: any, record: SpamLink) => (
        <Space>
          <Button
            type="text"
            icon={<EditOutlined />}
            onClick={() => navigate(`/spam-links/${record.id}`)}
          >
            Edit
          </Button>
          <Popconfirm
            title="Are you sure you want to delete this spam link?"
            onConfirm={() => handleDelete(record.id)}
            okText="Yes"
            cancelText="No"
          >
            <Button type="text" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <Title level={2}>Spam Links</Title>
        <Button type="primary" icon={<PlusOutlined />} onClick={handleCreate}>
          Create Spam Link
        </Button>
      </div>

      <Card>
        <Table
          columns={columns}
          dataSource={data?.data || []}
          loading={isLoading}
          rowKey="id"
          pagination={{
            current: currentPage,
            pageSize: pageSize,
            total: data?.total || 0,
            showSizeChanger: true,
            showTotal: (total) => `Total ${total} items`,
            onChange: (page, size) => {
              setCurrentPage(page);
              setPageSize(size || 10);
            },
          }}
        />
      </Card>

      <Modal
        title="Create Spam Link"
        open={isModalVisible}
        onOk={handleSubmit}
        onCancel={handleCancel}
        confirmLoading={createMutation.isLoading}
        width={600}
      >
        <Form
          form={form}
          layout="vertical"
          initialValues={{
            target_domains_count: 0,
          }}
        >
          <Form.Item
            name="domain_url"
            label="Domain URL"
            rules={[
              { required: true, message: 'Please enter domain URL' },
              { type: 'url', message: 'Please enter a valid URL' },
            ]}
          >
            <Input placeholder="https://example.com" />
          </Form.Item>

          <Form.Item
            name="target_domains_count"
            label="Number of Domains"
            rules={[
              { required: true, message: 'Please enter number of domains' },
              { type: 'number', min: 0, message: 'Must be at least 0' },
            ]}
          >
            <InputNumber
              min={0}
              style={{ width: '100%' }}
              placeholder="Enter number of domains that will redirect"
            />
          </Form.Item>

          <Form.Item
            name="description"
            label="Description (Optional)"
          >
            <Input.TextArea
              rows={3}
              placeholder="Optional description"
            />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default SpamLinks;

